import {useInfiniteQuery, type QueryFunctionContext, type UseInfiniteQueryOptions} from '@tanstack/react-query';
import type {AxiosError} from 'axios';

import {api} from '@eksab/api';
import {useAccessToken} from '@eksab/hooks/useAccessToken';

import type {Group, GroupsFilter} from '../types';

interface QueryParams {
  filter: GroupsFilter;
  query?: string;
}

interface Response {
  meta: {current_page: number; last_page: number};
  data: Group[];
}

const queryKey = ({filter, query = ''}: QueryParams, isLoggedIn: boolean) =>
  ['groups', filter, query, isLoggedIn] as const;

type QueryKey = ReturnType<typeof queryKey>;

const queryFn = ({queryKey: [_, filter, query, isLoggedIn], pageParam = 1}: QueryFunctionContext<QueryKey>) =>
  api
    .get<Response>(`v2/groups${isLoggedIn ? '/auth' : ''}`, {
      params: {
        per_page: 9,
        page: pageParam,
        q: query,
        ...(filter === 'mine' && {mine: true}),
      },
    })
    .then((res) => res.data);

export const useGroupsQuery = (
  params: QueryParams,
  options?: UseInfiniteQueryOptions<Response, AxiosError, Response, Response, QueryKey>,
) => {
  const isLoggedIn = useAccessToken().isSuccess;

  return useInfiniteQuery<Response, AxiosError, Response, QueryKey>(queryKey(params, isLoggedIn), queryFn, {
    ...options,
    getNextPageParam: ({meta}) => (meta.current_page < meta.last_page ? meta.current_page + 1 : undefined),
  });
};

useGroupsQuery.queryFn = queryFn;
useGroupsQuery.queryKey = queryKey;
